import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { reviewService, IReviewPoll, IMessage } from '../service/review.service';

interface UseNotificationsProps {
  review: IReviewPoll | null;
  myParticipantId: number | null;
  myName: string;
  isAdmin?: boolean;
  slug: string;
  chatOpen: boolean;
}

const requestPermission = () => {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
};

const sendNotification = async (title: string, body: string) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    // If service worker push is active, let it handle notifications
    if ('serviceWorker' in navigator) {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) return; // Push is active, server will send via push
      } catch {}
    }
    // Fallback: foreground notification
    new Notification(title, { body, icon: '/favicon.ico' });
  }
};

export const useNotifications = ({
  review,
  myParticipantId,
  myName,
  isAdmin,
  slug,
  chatOpen
}: UseNotificationsProps) => {
  const prevPositionRef = React.useRef<number | null>(null);
  const prevStatusRef = React.useRef<string | null>(null);
  const lastSeenMessageIdRef = React.useRef<number>(0);
  const hasInitializedRef = React.useRef(false);
  const [unreadCount, setUnreadCount] = React.useState(0);

  // Request notification permission
  React.useEffect(() => {
    if (myParticipantId || isAdmin) {
      requestPermission();
    }
  }, [myParticipantId, isAdmin]);

  // Watch for position/status changes to notify "next up" (participants only)
  React.useEffect(() => {
    if (!review || !myParticipantId) return;

    const me = review.participants?.find((p) => p.id === myParticipantId);
    if (!me) return;

    if (!hasInitializedRef.current) {
      prevPositionRef.current = me.position;
      prevStatusRef.current = me.status;
      hasInitializedRef.current = true;
      return;
    }

    const waitingSorted = review.participants
      .filter((p) => p.status === 'waiting')
      .sort((a, b) => a.position - b.position);
    const myIdx = waitingSorted.findIndex((p) => p.id === me.id);

    const wasNextUp = prevPositionRef.current === 0 && prevStatusRef.current === 'waiting';
    const isNextUp = myIdx === 0 && me.status === 'waiting';

    if (isNextUp && !wasNextUp) {
      sendNotification('You\'re next up!', `Get ready, you're next in "${review.title}"`);
    }

    if (me.status === 'active' && prevStatusRef.current !== 'active') {
      sendNotification('It\'s your turn!', `You're now in the meeting for "${review.title}"`);
    }

    if (myIdx === 1 && me.status === 'waiting' && prevPositionRef.current !== 1) {
      sendNotification('You\'re on standby', `You're almost up in "${review.title}"`);
    }

    prevPositionRef.current = me.position;
    prevStatusRef.current = me.status;
  }, [review, myParticipantId]);

  // Poll chat messages — enabled for both participants and admins
  const { data: messages } = useQuery<IMessage[] | void>({
    queryKey: ['chat-notifications', slug],
    queryFn: () => reviewService.getMessages(slug),
    refetchInterval: 5000,
    enabled: !!slug && !!myName && (!!myParticipantId || !!isAdmin)
  });

  // When chat is opened, mark everything as read
  React.useEffect(() => {
    if (chatOpen && messages && Array.isArray(messages)) {
      if (messages.length > 0) {
        lastSeenMessageIdRef.current = messages[messages.length - 1].id;
      }
      setUnreadCount(0);
    }
  }, [chatOpen, messages]);

  // Track new messages and count unreads + send notifications
  React.useEffect(() => {
    if (!messages || !Array.isArray(messages) || !myName) return;
    if (messages.length === 0) return;

    // Initialize last seen on first load
    if (lastSeenMessageIdRef.current === 0) {
      lastSeenMessageIdRef.current = messages[messages.length - 1].id;
      return;
    }

    const newMessages = messages.filter(
      (m) => m.id > lastSeenMessageIdRef.current && m.sender !== myName
    );

    if (chatOpen) {
      if (messages.length > 0) {
        lastSeenMessageIdRef.current = messages[messages.length - 1].id;
      }
      setUnreadCount(0);
    } else if (newMessages.length > 0) {
      setUnreadCount((prev) => prev + newMessages.length);
      lastSeenMessageIdRef.current = messages[messages.length - 1].id;
    }

    // Check for @mentions in new messages
    for (const msg of newMessages) {
      const escaped = myName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const mentionPattern = new RegExp(`@(?:${escaped}|here${isAdmin ? '|admin' : ''}|team)(?:\\s|$|[.,!?])`, 'i');
      if (mentionPattern.test(msg.text)) {
        const isHere = /@here(?:\s|$|[.,!?])/i.test(msg.text);
        const isTeam = /@team(?:\s|$|[.,!?])/i.test(msg.text);
        const isAdminMention = /@admin(?:\s|$|[.,!?])/i.test(msg.text);
        sendNotification(
          isHere ? `${msg.sender} mentioned everyone`
            : isTeam ? `${msg.sender} mentioned the team`
            : isAdminMention ? `${msg.sender} mentioned admins`
            : `${msg.sender} mentioned you`,
          msg.text
        );
      }
    }
  }, [messages, myName, chatOpen, isAdmin]);

  return { unreadCount };
};
